/* AppraisalComponents/FoamFillerBar.jsx
 * Phase C · C4e Sheet-adjacent extraction (2026-02c).
 * Extracted verbatim from AppraisalComponentsTab.js:4978-5084.
 * Foam-filler bar for the Sheet section (auto-line manager).
 */
import React, { useState, useEffect } from 'react';

export function FoamFillerBar({ lines, rates, onCommit }) {
  const PROFILES = ['Corrugate', 'RibLine', 'MultiRib', 'TrimRib', 'Maxispan', 'RT7'];
  const FLAT_KEYS = ['Flat 40x25', 'Flat 25x25', 'Flat 50x50'];
  const lineList = lines || [];

  // Detect which profiles are actually on this appraisal (non-auto sheet lines).
  const detected = PROFILES.filter(p =>
    lineList.some(l => !/^\[AUTO:/i.test(l.notes || '') &&
                       (l.profile || '').toLowerCase() === p.toLowerCase())
  );

  // Read saved state from auto-lines.
  const findSaved = (profile, tip) => lineList.find(l =>
    (l.notes || '').toLowerCase().startsWith(`[auto:foam_filler_${profile.toLowerCase()}_${tip}`)
  );
  const initialSelections = {};
  PROFILES.forEach(p => {
    const hasNarrow = !!findSaved(p, 'narrow') || !!findSaved(p, 'corrugate');
    const hasWide   = !!findSaved(p, 'wide');
    initialSelections[p] = hasNarrow && hasWide ? 'both' : hasNarrow ? 'narrow' : hasWide ? 'wide' : 'none';
  });
  const initialFlat = {};
  FLAT_KEYS.forEach(k => {
    const ln = lineList.find(l => (l.notes || '').toLowerCase().includes(`foam_filler_${k.replace(/[^a-z0-9]/gi,'_').toLowerCase()}`));
    initialFlat[k] = ln ? String(Number(ln.quantity) || '') : '';
  });

  const [selections, setSelections] = React.useState(initialSelections);
  const [flat, setFlat] = React.useState(initialFlat);
  React.useEffect(() => { setSelections(initialSelections); /* eslint-disable-next-line */ }, [lineList.length]);
  React.useEffect(() => { setFlat(initialFlat); /* eslint-disable-next-line */ }, [lineList.length]);

  if (detected.length === 0 && lineList.length === 0) return null;
  // Only show if any sheets are present (covers the empty-flat-only case too).
  if (detected.length === 0 && !lineList.some(l => !/^\[AUTO:/i.test(l.notes||''))) return null;

  const sheetCountFor = (profile) => lineList
    .filter(l => !/^\[AUTO:/i.test(l.notes||'') && (l.profile||'').toLowerCase() === profile.toLowerCase())
    .reduce((acc, l) => acc + (Number(l.quantity) || 0), 0);

  const commit = (newSelections, newFlat) => {
    onCommit({ selections: newSelections, flat: newFlat });
  };
  const setProfile = (p, tip) => {
    const next = { ...selections, [p]: tip };
    setSelections(next);
    commit(next, flat);
  };
  const setFlatMeters = (k, v) => {
    const next = { ...flat, [k]: v };
    setFlat(next);
  };
  const commitFlat = () => commit(selections, flat);

  return (
    <div data-testid="foam-filler-bar" className="rounded-md border border-rose-200 bg-rose-50/40 px-3 py-2 space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700">
        🌫️ Foam Fillers (Sheet) — 1 per sheet
      </div>
      {detected.length === 0 ? (
        <div className="text-[10px] text-slate-500 italic">No matching sheet profiles in this appraisal.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {detected.map(profile => {
            const sc = sheetCountFor(profile);
            const sel = selections[profile] || 'none';
            return (
              <div key={profile} className="rounded border border-rose-100 bg-white p-2 flex items-center gap-2 flex-wrap">
                <div className="flex-1">
                  <div className="text-[11px] font-bold text-slate-700">{profile}</div>
                  <div className="text-[10px] text-slate-500">{sc} sheet{sc !== 1 ? 's' : ''}</div>
                </div>
                {['none', 'narrow', 'wide', 'both'].map(opt => (
                  <button
                    key={opt} type="button"
                    onClick={() => setProfile(profile, opt)}
                    data-testid={`foam-${profile.toLowerCase()}-${opt}`}
                    className={`text-[10px] px-2 py-0.5 rounded font-semibold ${sel===opt ? 'bg-rose-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'}`}
                  >{opt}</button>
                ))}
              </div>
            );
          })}
        </div>
      )}
      <div className="pt-1 border-t border-rose-200">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Flat strips (operator-entered meters)</div>
        <div className="grid grid-cols-3 gap-2">
          {FLAT_KEYS.map(k => (
            <label key={k} className="block">
              <span className="block text-[10px] text-slate-500">{k}</span>
              <input
                type="number" min={0} step={0.1}
                value={flat[k] || ''}
                onChange={(e) => setFlatMeters(k, e.target.value)}
                onBlur={commitFlat}
                data-testid={`foam-flat-${k.replace(/[^a-z0-9]/gi,'-').toLowerCase()}`}
                placeholder="m"
                className="w-full text-xs border border-slate-300 rounded px-2 py-1 font-mono-num bg-white"
              />
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
